import { logger } from '@gik/analytics/utils/logger';
import FileUtils, { AcceptType } from '@gik/core/utils/FileUtils';
import vCard from 'vcf';

/**
 * A single imported contact, shaped to drop straight into a creatable-select /
 * tags input (`{ label, value }`). Both fields hold the email address.
 */
export type ContactEmailOption = { label: string; value: string };

/** Reads the email addresses out of a vCard (.vcf) file's text. */
function extractEmailsFromVCard(text: string): string[] {
  // vcf's typings don't model the parsed card shape well.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: any[] = vCard.parse(text);

  return items
    .map(card => card.data.email?._data || card.data.email?.[0]?._data)
    .filter((item: string | undefined) => item !== undefined);
}

/**
 * Opens the OS file picker for a vCard (.vcf) file, parses it, and returns the
 * contained email addresses as `{ label, value }` options ready to merge into
 * an email tags input.
 *
 * Returns `null` when the user cancels the picker (or the file can't be read),
 * so callers can distinguish "cancelled" from "found no emails" (`[]`).
 */
export async function importContactEmails(): Promise<ContactEmailOption[] | null> {
  const attachResult = await FileUtils.attachContacts([AcceptType.VCF]);

  if (!attachResult) return null;

  try {
    const emails = attachResult.mimetype === 'text/vcard' ? extractEmailsFromVCard(attachResult.text) : [];

    return emails.map(email => ({ label: email, value: email }));
  } catch (error) {
    logger.error(error);
    return null;
  }
}
